import { useEffect, useMemo, useState } from 'react';

import { useGetTemplate } from '@/components/templates-v2/document-generation/hooks/useGetTemplate';
import { validateTemplateFields } from '@/components/treatment/v3/request/package-edit/utils/fieldValidation';
import { enhancedApi as deptApi } from '@/services/api/generated/policeDepartmentsService';
import { useTemplateListQuery } from '@/services/api/generated/templatesV2Service';
import { useGetMatterFlattenedContextQuery } from '@/services/api/matterFlattenedContextService';

export const useMailingData = ({
  isOpen,
  isFoia,
  departmentId,
  matterId,
  policeReportId,
  firmId,
}: {
  isOpen: boolean;
  isFoia: boolean;
  departmentId: string;
  matterId: string;
  policeReportId: string;
  firmId?: string;
}) => {
  const [selectedChannelIdx, setSelectedChannelIdx] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  // Fetch channels
  const { data: allChannels = [] } = deptApi.usePoliceRecordsChannelsListQuery(
    { departmentId },
    { skip: !isOpen || !departmentId },
  );

  const mailChannels = useMemo(
    () =>
      allChannels.filter(
        (c) => c.channel_type === 'mail' && c.mailing_instructions,
      ),
    [allChannels],
  );

  // Auto-select best channel
  const bestChannelIdx = useMemo(() => {
    if (mailChannels.length === 0) return 0;
    const preferredServices = ['crash_reports', 'general_records'];
    const idx = mailChannels.findIndex((c) =>
      c.services_provided?.some((s) => preferredServices.includes(s)),
    );
    return idx >= 0 ? idx : 0;
  }, [mailChannels]);

  useEffect(() => {
    setSelectedChannelIdx(bestChannelIdx);
  }, [bestChannelIdx]);

  const selectedChannel = mailChannels[selectedChannelIdx];

  // FOIA templates
  const { data: matterContext } = useGetMatterFlattenedContextQuery(
    { matterId, policeReportId },
    { skip: !isFoia || !isOpen },
  );

  const {
    data: templatesData,
    isLoading: templatesLoading,
    refetch: refetchTemplates,
  } = useTemplateListQuery(
    { firmId: firmId || '', category: 'FOIA_LETTER', scope: 'FIRM', status: 'ACTIVE' },
    { skip: !isFoia || !firmId || !isOpen },
  );
  const templates = useMemo(
    () =>
      (templatesData?.results ?? []).filter(
        (t) => t.format !== 'EXTERNAL' && t.format !== 'SMS',
      ),
    [templatesData],
  );

  const { template } = useGetTemplate(selectedTemplateId);

  useEffect(() => {
    if (isFoia && templates.length > 0 && !selectedTemplateId) {
      setSelectedTemplateId(templates[0].id);
    }
  }, [templates, selectedTemplateId, isFoia]);

  useEffect(() => {
    setOverrides({});
  }, [selectedTemplateId]);

  const validation = useMemo(() => {
    if (!isFoia || !template || !matterContext) {
      return { isValid: true, missingFields: [], missingCount: 0 };
    }
    return validateTemplateFields(
      template.fields ?? [],
      matterContext as Record<string, string | undefined>,
      overrides,
    );
  }, [template, matterContext, overrides, isFoia]);

  const chequeAmount = useMemo(() => {
    if (!selectedChannel || isFoia) return 0;
    const feeStructure = selectedChannel.mailing_instructions?.fee_structure;
    return feeStructure ? (feeStructure.estimated_fee_cents || 0) / 100 : 0;
  }, [selectedChannel, isFoia]);

  return {
    mailChannels,
    selectedChannelIdx,
    setSelectedChannelIdx,
    selectedChannel,
    templates,
    templatesLoading,
    refetchTemplates,
    selectedTemplateId,
    setSelectedTemplateId,
    matterContext,
    overrides,
    setOverrides,
    validation,
    chequeAmount,
  };
};
